import React, { useCallback, useEffect, useState } from 'react';
import { Calendar, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { useLanguage } from '../../context/LanguageContext';
import { guestViewingSlotsAPI } from '../../lib/guestViewingSlotsApi';
import { extractApiErrorMessage } from '../../lib/ownerViewingSlotForm';
import { DEFAULT_VIEWING_TIMEZONE, formatSlotDateTime } from '../../lib/ownerViewingSlotForm';
import { viewingStateLabel } from '../../lib/bookingInquiryDisplay';
import {
  canShowGuestReserveActions,
  formatSlotDate,
  formatSlotTimeRange,
  isViewingAlreadyReserved,
  mapReserveConflictMessage,
  remainingSpots,
  resolveGuestViewingSectionState,
  sanitizeGuestSlotForDisplay,
} from '../../lib/guestViewingSlotDisplay';

const SLOT_STATUS_STYLES = {
  offered: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30',
  reserved: 'bg-amber-500/15 text-amber-700 border-amber-500/30',
};

function slotStatusClass(status) {
  return SLOT_STATUS_STYLES[String(status || '').toLowerCase()] || SLOT_STATUS_STYLES.offered;
}

export default function GuestViewingSlotSelector({ inquiry, onViewingReserved }) {
  const { t } = useLanguage();
  const inquiryId = inquiry?.id;
  const viewingState = inquiry?.viewing_state;

  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [reservingId, setReservingId] = useState(null);
  const [reservedSlot, setReservedSlot] = useState(null);

  const loadSlots = useCallback(async () => {
    if (!inquiryId) {
      setSlots([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const res = await guestViewingSlotsAPI.list(inquiryId);
      const next = (res.data?.slots || []).map(sanitizeGuestSlotForDisplay);
      setSlots(next);
    } catch (error) {
      console.error('Failed to load guest viewing slots:', error);
      setLoadError(
        extractApiErrorMessage(error, t('booking.viewing_load_failed', 'Could not load viewing slots.')),
      );
      setSlots([]);
    } finally {
      setLoading(false);
    }
  }, [inquiryId, t]);

  useEffect(() => {
    loadSlots();
  }, [loadSlots, viewingState]);

  const handleReserve = async (slot) => {
    if (!inquiryId || !slot?.id || reservingId) {
      return;
    }
    setReservingId(slot.id);
    try {
      const res = await guestViewingSlotsAPI.reserve(inquiryId, slot.id);
      const bookedSlot = sanitizeGuestSlotForDisplay(res.data?.slot);
      if (bookedSlot?.id) {
        setReservedSlot(bookedSlot);
      }
      toast.success(
        t('booking.viewing_reserve_success', 'Viewing reserved. Check your notifications for confirmation.'),
      );
      await loadSlots();
      onViewingReserved?.();
    } catch (error) {
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      const friendly = mapReserveConflictMessage(detail, t);
      if (status === 409 && friendly) {
        toast.error(friendly);
        await loadSlots();
      } else {
        toast.error(
          extractApiErrorMessage(
            error,
            t('booking.viewing_reserve_failed', 'Could not reserve viewing slot.'),
          ),
        );
      }
    } finally {
      setReservingId(null);
    }
  };

  const sectionState = resolveGuestViewingSectionState({
    slots,
    loading,
    error: loadError,
    viewingState,
  });

  const showReserve = canShowGuestReserveActions(viewingState);
  const displaySlot = reservedSlot || (isViewingAlreadyReserved(viewingState) ? slots[0] : null);

  return (
    <div
      className="mt-4 pt-4 border-t border-slate-100"
      data-testid={`guest-viewing-slots-${inquiryId}`}
    >
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <Calendar className="w-4 h-4 text-slate-500 shrink-0" aria-hidden />
        <h4 className="text-sm font-semibold text-slate-800">
          {t('booking.viewing_slots_title', 'Viewing slots')}
        </h4>
        <Badge variant="outline" className="text-[10px] border-slate-200 text-slate-600">
          {viewingStateLabel(viewingState, t)}
        </Badge>
      </div>

      {sectionState.mode === 'loading' && (
        <div className="flex items-center gap-2 text-xs text-slate-500" data-testid="guest-viewing-slots-loading">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          {t('common.loading', 'Loading...')}
        </div>
      )}

      {sectionState.mode === 'error' && (
        <p className="text-xs text-red-600" data-testid="guest-viewing-slots-error" role="alert">
          {loadError}
        </p>
      )}

      {sectionState.mode === 'reserved' && (
        <div className="text-xs text-slate-600 space-y-1" data-testid="guest-viewing-reserved">
          <p className="font-medium text-emerald-700">
            {t('booking.viewing_reserved', 'Viewing reserved')}
          </p>
          {displaySlot?.start_time && (
            <p className="text-slate-700">
              {formatSlotDate(displaySlot.start_time)}
              {' · '}
              {formatSlotTimeRange(displaySlot.start_time, displaySlot.end_time)}
              {displaySlot.timezone ? ` (${displaySlot.timezone})` : ''}
            </p>
          )}
        </div>
      )}

      {sectionState.mode === 'empty' && (
        <p className="text-xs text-slate-500" data-testid="guest-viewing-slots-empty">
          {t('booking.viewing_slots_empty', 'No viewing slots offered yet.')}
        </p>
      )}

      {sectionState.mode === 'slots' && (
        <>
          <p className="text-xs text-emerald-700 mb-2" data-testid="guest-viewing-slots-available">
            {t('booking.viewing_slots_available', 'Viewing slots available')}
          </p>
          <ul className="space-y-2">
            {slots.map((slot) => {
              const spotsLeft = remainingSpots(slot);
              const canReserve = showReserve && slot.has_availability !== false && spotsLeft > 0;
              return (
                <li
                  key={slot.id}
                  className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs"
                  data-testid={`guest-viewing-slot-${slot.id}`}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium text-slate-800">
                        {formatSlotDate(slot.start_time) || formatSlotDateTime(slot.start_time)}
                      </p>
                      <p className="text-slate-600 mt-0.5">
                        {formatSlotTimeRange(slot.start_time, slot.end_time)}
                      </p>
                      <p className="text-slate-500 mt-0.5">
                        {t('owner.viewing_timezone', 'Timezone')}: {slot.timezone || DEFAULT_VIEWING_TIMEZONE}
                        {' · '}
                        {t('owner.viewing_capacity', 'Capacity')}: {slot.capacity}
                        {spotsLeft != null && (
                          <>
                            {' · '}
                            {t('booking.viewing_remaining_spots', 'Remaining spots')}: {spotsLeft}
                          </>
                        )}
                      </p>
                    </div>
                    <div className="flex flex-col items-start sm:items-end gap-1 shrink-0">
                      <Badge className={`text-[10px] border ${slotStatusClass(slot.status)}`}>
                        {slot.status || 'offered'}
                      </Badge>
                      {showReserve && (
                        <Button
                          type="button"
                          size="sm"
                          className="h-7 text-xs bg-gradient-to-r from-gold-600 to-amber-600 hover:from-gold-500 hover:to-amber-500"
                          disabled={!canReserve || reservingId === slot.id}
                          onClick={() => handleReserve(slot)}
                          data-testid={`reserve-viewing-${slot.id}`}
                        >
                          {reservingId === slot.id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            t('booking.viewing_reserve', 'Reserve viewing')
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}
